import React, { useState } from "react";
import { Input } from "../input";

// Dummy customer data
const initialCustomers = [
    {
        id: "C001",
        name: "Test Testesen",
        uid: "custm_ur8cz5vt",
        email: "nouman@chamcore.com",
        phone: "+47 96702167",
        address: "Portveien 2",
        city: "Oslo",
        county: "Oslo",
        postalCode: "0121",
        country: "Norge",
        totalSpend: 9245,
        totalMinutes: 121,
        lastActive: "I dag 13:02",
        paymentMethods: ["Stripe", "Vipps"],
        status: "active",
    },
    {
        id: "C002",
        name: "Kari Nordmann",
        uid: "custm_ab12cd34",
        email: "kari@example.com",
        phone: "+47 40112233",
        address: "Karl Johans gate 5",
        city: "Oslo",
        county: "Oslo",
        postalCode: "0154",
        country: "Norge",
        totalSpend: 3120,
        totalMinutes: 45,
        lastActive: "I går 21:15",
        paymentMethods: ["Vipps"],
        status: "active",
    },
    {
        id: "C003",
        name: "Ola Hansen",
        uid: "custm_ef56gh78",
        email: "ola@example.com",
        phone: "+47 99887766",
        address: "Storgata 12",
        city: "Trondheim",
        county: "Trøndelag",
        postalCode: "7013",
        country: "Norge",
        totalSpend: 890,
        totalMinutes: 12,
        lastActive: "3 dager siden",
        paymentMethods: ["Stripe"],
        status: "inactive",
    },
];

function statusLabel(status: string) {
    if (status === "active") return "Aktiv";
    if (status === "inactive") return "Deaktivert";
    return status;
}

export default function CustomersModule({tab, setTab}: {tab: string, setTab: (tab: string) => void}) {
    const [customers, setCustomers] = useState(initialCustomers);
    const [selectedId, setSelectedId] = useState<string | null>(initialCustomers[0]?.id ?? null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [timeRange, setTimeRange] = useState("day");

    const selectedCustomer = customers.find((c) => c.id === selectedId) || customers[0];

    const filtered = customers.filter((c) => {
        const q = search.toLowerCase();
        const matchesSearch =
            !q ||
            c.name.toLowerCase().includes(q) ||
            c.email.toLowerCase().includes(q) ||
            c.phone.toLowerCase().includes(q) ||
            c.uid.toLowerCase().includes(q);
        const matchesStatus = statusFilter === "all" || c.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalCustomers = customers.length;
    const activeCustomers = customers.filter((c) => c.status === "active").length;
    const revenueTotal = customers.reduce((sum, c) => sum + c.totalSpend, 0);
    const minutesTotal = customers.reduce((sum, c) => sum + c.totalMinutes, 0);

    // Mock actions – can be wired to API later
    const sendOtp = () => {
        if (!selectedCustomer) return;
        alert(`Engangskode sendt til ${selectedCustomer.phone}`);
    };

    const sendReceipt = () => {
        if (!selectedCustomer) return;
        alert(`Kvittering sendt til ${selectedCustomer.email}`);
    };

    const triggerRefund = () => {
        if (!selectedCustomer) return;
        const confirmRefund = window.confirm(
            `Start refusjon til ${selectedCustomer.name}? Dette krever bekreftelse i Stripe/Vipps.`
        );
        if (confirmRefund) {
            alert("Refusjon sendt til behandlingskø.");
        }
    };

    const toggleStatus = (id: string) => {
        setCustomers((prev) =>
            prev.map((c) => (c.id === id ? { ...c, status: c.status === "active" ? "inactive" : "active" } : c))
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-8">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Kunder</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Administrer kunder, historikk, innlogging og betalinger.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="inline-flex items-center rounded-full bg-slate-100 p-1 text-xs">
                        <button
                            onClick={() => setTimeRange("day")}
                            className={`px-3 py-1 rounded-full ${timeRange === "day" ? "bg-white shadow text-slate-900" : "text-slate-500"
                                }`}
                        >
                            I dag
                        </button>
                        <button
                            onClick={() => setTimeRange("week")}
                            className={`px-3 py-1 rounded-full ${timeRange === "week" ? "bg-white shadow text-slate-900" : "text-slate-500"
                                }`}
                        >
                            Uke
                        </button>
                        <button
                            onClick={() => setTimeRange("month")}
                            className={`px-3 py-1 rounded-full ${timeRange === "month" ? "bg-white shadow text-slate-900" : "text-slate-500"
                                }`}
                        >
                            Måned
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Totalt antall kunder</p>
                    <p className="text-2xl font-semibold text-slate-900">{totalCustomers}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Aktive kunder</p>
                    <p className="text-2xl font-semibold text-emerald-600">{activeCustomers}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Akkumulert omsetning (NOK)</p>
                    <p className="text-2xl font-semibold text-slate-900">{revenueTotal.toLocaleString("nb-NO")}</p>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Total samtaletid (minutter)</p>
                    <p className="text-2xl font-semibold text-slate-900">{minutesTotal}</p>
                </div>
            </div>

            {/* MAIN LAYOUT */}
            <div className="grid grid-cols-1 gap-6 items-start">
                {/* LEFT: CUSTOMER LIST */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
                        <div className="flex items-center gap-2 flex-1">
                            <Input
                                type="text"
                                placeholder="Søk på navn, e-post, telefon eller UID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                            />
                        </div>

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-slate-50"
                        >
                            <option value="all">Alle statuser</option>
                            <option value="active">Aktive</option>
                            <option value="inactive">Deaktiverte</option>
                        </select>
                    </div>

                    <div className="overflow-x-auto md:overflow-visible">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-slate-500 bg-slate-50">
                                    <th className="text-left px-4 py-2">Kunde</th>
                                    <th className="text-left px-4 py-2">Telefon</th>
                                    <th className="text-left px-4 py-2">E-post</th>
                                    <th className="text-left px-4 py-2">Omsetning</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => (
                                    <tr
                                        key={c.id}
                                        onClick={() => setSelectedId(c.id)}
                                        className={`border-t border-slate-100 cursor-pointer hover:bg-indigo-50/40 transition ${selectedId === c.id ? "bg-indigo-50/60" : "bg-white"
                                            }`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900" onClick={() => setTab("Customer detail")}>{c.name}</div>
                                            <div className="text-xs text-slate-500">UID: {c.uid}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{c.phone}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">{c.email}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">{c.totalSpend.toLocaleString("nb-NO")} kr</td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.status === "active"
                                                        ? "bg-emerald-50 text-emerald-700"
                                                        : "bg-slate-100 text-slate-500"
                                                    }`}
                                            >
                                                {statusLabel(c.status)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={5}>
                                            Ingen kunder matcher søket.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* FULL-WIDTH DETAIL BELOW LIST */}
                {selectedCustomer && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 w-full flex flex-col gap-6">
                        {/* existing detail content unchanged */}
                        {/* ... */}
                    </div>
                )}
            </div>
        </div>
    );
}
